import { ProvisionPermissions } from '@core/models/provision/permissions'

export type MenuItemType = {
  key: string
  label: string
  isTitle?: boolean
  icon?: string
  url?: string
  badge?: {
    variant: string
    text: string
  }
  parentKey?: string
  isDisabled?: boolean
  collapsed?: boolean
  children?: MenuItemType[]
  // When set, the entry renders only if the user's resolved permissions contain this literal (FEAT-0004
  // AC-5). UX only — the route guard and the API still enforce. Entries without it show for all staff.
  permission?: string
}

/**
 * Menu entries the user may see: entries carrying a `permission` are kept only when the user's resolved
 * permission list contains it; children are filtered the same way (a parent whose children are all hidden
 * and which has no url of its own is dropped). UX only — routes and the API re-enforce.
 */
export function filterMenuByPermissions(
  items: MenuItemType[],
  permissions: string[] | null | undefined
): MenuItemType[] {
  const held = permissions ?? []
  return items
    .filter((item) => !item.permission || held.includes(item.permission))
    .map((item) =>
      item.children ? { ...item, children: filterMenuByPermissions(item.children, held) } : item
    )
    .filter((item) => !item.children || item.children.length > 0 || !!item.url || !!item.isTitle)
}

export type SubMenus = {
  item: MenuItemType
  linkClassName?: string
  subMenuClassName?: string
  activeMenuItems?: Array<string>
  toggleMenu?: (item: MenuItemType, status: boolean) => void
  className?: string
}

export const MENU_ITEMS: MenuItemType[] = [
  {
    key: 'nav',
    label: 'Navigation',
    isTitle: true,
  },
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'ri-dashboard-3-line',
    url: '/index',
  },
  {
    key: 'platform',
    label: 'Platform',
    isTitle: true,
  },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    icon: 'ri-bookmark-3-line',
    url: '/subscriptions',
  },
  {
    key: 'organizations',
    label: 'Organizations',
    icon: 'ri-building-line',
    url: '/organizations',
  },
  {
    key: 'subscription-plans',
    label: 'Subscription Plans',
    icon: 'ri-price-tag-3-line',
    url: '/subscription-plans',
    // FEAT-0005 AC-1 (D1 aggressive UI): absent from navigation without the permission.
    permission: ProvisionPermissions.ViewSubscriptionPlans,
  },
  {
    key: 'users',
    label: 'Users',
    icon: 'ri-user-line',
    // Children carry the gate; a parent with no url of its own drops when all children are hidden.
    children: [
      {
        key: 'staff-users',
        label: 'Staff',
        icon: 'ri-shield-user-line',
        url: '/users/staff',
        parentKey: 'users',
        permission: ProvisionPermissions.ViewUsers,
      },
      {
        key: 'customer-users',
        label: 'Customers',
        icon: 'ri-user-3-line',
        url: '/users/customers',
        parentKey: 'users',
        permission: ProvisionPermissions.ViewUsers,
      },
    ],
  },
  {
    key: 'roles',
    label: 'Roles',
    icon: 'ri-shield-keyhole-line',
    url: '/roles',
    // FEAT-0005 AC-1 (D1 aggressive UI): absent from navigation without the permission.
    permission: ProvisionPermissions.ViewRoles,
  },
  {
    key: 'audit',
    label: 'Audit trail',
    icon: 'ri-history-line',
    url: '/audit',
    // FEAT-0004 AC-5: absent from navigation without the permission (route + API still enforce).
    permission: ProvisionPermissions.ViewAuditTrail,
  },
]

export const HORIZONTAL_MENU_ITEM: MenuItemType[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'ri-dashboard-3-line',
    url: '/index',
  },
  {
    key: 'platform',
    label: 'Platform',
    icon: 'ri-stack-line',
    children: [
      {
        key: 'subscriptions',
        label: 'Subscriptions',
        icon: 'ri-bookmark-3-line',
        url: '/subscriptions',
        parentKey: 'platform',
      },
      {
        key: 'organizations',
        label: 'Organizations',
        icon: 'ri-building-line',
        url: '/organizations',
        parentKey: 'platform',
      },
      {
        key: 'subscription-plans',
        label: 'Subscription Plans',
        icon: 'ri-price-tag-3-line',
        url: '/subscription-plans',
        parentKey: 'platform',
        permission: ProvisionPermissions.ViewSubscriptionPlans,
      },
      {
        key: 'users',
        label: 'Users',
        icon: 'ri-user-line',
        parentKey: 'platform',
        children: [
          {
            key: 'staff-users',
            label: 'Staff',
            icon: 'ri-shield-user-line',
            url: '/users/staff',
            parentKey: 'users',
            permission: ProvisionPermissions.ViewUsers,
          },
          {
            key: 'customer-users',
            label: 'Customers',
            icon: 'ri-user-3-line',
            url: '/users/customers',
            parentKey: 'users',
            permission: ProvisionPermissions.ViewUsers,
          },
        ],
      },
      {
        key: 'roles',
        label: 'Roles',
        icon: 'ri-shield-keyhole-line',
        url: '/roles',
        parentKey: 'platform',
        permission: ProvisionPermissions.ViewRoles,
      },
      {
        key: 'audit',
        label: 'Audit trail',
        icon: 'ri-history-line',
        url: '/audit',
        parentKey: 'platform',
        // FEAT-0004 AC-5: absent from navigation without the permission (route + API still enforce).
        permission: ProvisionPermissions.ViewAuditTrail,
      },
    ],
  },
]
